import { useEffect, useRef } from 'react';

import { API, ResponseStatus, useFetch } from 'app/api';

const INTERVAL = 3000;

type UseTelegramSignArgs = {
  onSuccess?: () => void;
  onProcessing?: () => void;
  onFail?: () => void;
};

type Status = 'POLING' | 'FAILED_LOAD_CONFIG' | 'READY' | 'FAILED_SESSION_EXPIRED' | 'SUCCESS';

type UseTelegramSignReturnType = {
  url: string;
  status: Status;
  loading: boolean;
  start: () => void;
};

export function useTelegramSign({
  onSuccess,
  onProcessing,
  onFail,
}: UseTelegramSignArgs): UseTelegramSignReturnType {
  const intervalRef = useRef<NodeJS.Timer>();
  const statusRef = useRef<Status>('READY');
  const setStatus = (val: Status) => (statusRef.current = val);

  const {
    data: config,
    error: configLoadError,
    loading: configLoading,
    requested: configRequested,
    makeRequest: getConfig,
  } = useFetch(API.telegramSign.getTelegramAuthConfig, {
    skipSSR: true,
  });

  const intervalWorker = async () => {
    if (!config) {
      return;
    }
    const status = statusRef.current;

    if (
      status === 'FAILED_LOAD_CONFIG'
      || status === 'FAILED_SESSION_EXPIRED'
      || status === 'SUCCESS'
    ) {
      clearInterval(intervalRef.current);
      onSuccess?.();
      return;
    }

    const response = await API.telegramSign.tryTelegramAuth(config.startGuid);

    if (response.status === ResponseStatus.SUCCESS) {
      if (response.data.type === 'sessionInProcess') {
        return;
      }

      if (response.data.type === 'sessionExpired') {
        setStatus('FAILED_SESSION_EXPIRED');
        onFail?.();
        return;
      }

      setStatus('SUCCESS');
      onSuccess?.();
    }
  };

  const start = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (config) {
      onProcessing?.();
      setStatus('POLING');
      intervalRef.current = setInterval(intervalWorker, INTERVAL);
    }
  };

  useEffect(() => {
    if (!configRequested) {
      getConfig();
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (
      (configLoadError && status !== 'FAILED_LOAD_CONFIG')
      || (configRequested && !configLoading && !config)
    ) {
      setStatus('FAILED_LOAD_CONFIG');
    }
  }, [configLoadError, status, configRequested, configLoading, config]);

  return {
    url: !config ? '' : `${config.botLink}?start=${config.startGuid}`,
    status: statusRef.current,
    loading: configLoading,
    start,
  };
}
